import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { Container, Row, Col, FormGroup, Button, Input } from 'reactstrap';

import SelectList from 'react-widgets/lib/SelectList';
import Multiselect from 'react-widgets/lib/Multiselect';
import 'react-widgets/dist/css/react-widgets.css';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';

import ListeningRoute from '../ListeningRoute';
import EnableWhen from './../../containers/EnableWhen/EnableWhen';
import './Questionnaire.css';
import caretDown from '../../assets/img/caretDown.svg';
import NoWriteDateInput from '../NoWriteDateInput'

import TextareaAutosize from 'react-autosize-textarea';

// styling for spacing between questions
const QuestionStyle = {
  margin: 15
};

const InputProps = {
  input: PropTypes.shape({}).isRequired,
  type: PropTypes.string.isRequired,
  meta: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

const DateProps = {
  input: PropTypes.shape({}).isRequired,
  type: PropTypes.string.isRequired,
  meta: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

const ChoiceProps = {
  input: PropTypes.shape({}).isRequired,
  data: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]).isRequired,
  valueField: PropTypes.string.isRequired,
  textField: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  meta: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

const QuestionProps = {
  data: PropTypes.shape({
    repeats: PropTypes.bool,
  }).isRequired,
};
const QuestionnaireProps = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    item: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

function getCurrentQuestionnaireName() {
  const pathName = window.location.pathname;
  const questionnaireName = pathName.split("/")[2];
  return questionnaireName;
}

// component for rendering an input field (comment or text)
const renderInput = ({ input, type, meta: { touched, error, warning } }) => (
  <div>
        <TextareaAutosize {...input} type={type} TextMode="MultiLine" className="inputStyle"/>
    <div>
      {touched &&
        ((error && <span>{error}</span>) ||
          (warning && <span>{warning}</span>))}
    </div>
  </div>
);

// component for rendering a date select field
const renderDateSelect = ({
  input,
  type,
  meta: { touched, error, warning },
}) => (
  <div>
    <DatePicker
      {...input}
      type={type}
      onBlur={() => input.onBlur(input.value)}
      dateFormat="DD-MM-YYYY"
      onChange={date => input.onChange(moment(date).format('DD-MM-YYYY'))}
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
      autoComplete="off"
      maxDate={moment()}
      placeholderText="Click to select a date"
      selected={input.value ? moment(input.value, 'DD-MM-YYYY') : null}
      normalize={value => (value ? moment(value).format('DD-MM-YYYY') : null)}
      customInput={<NoWriteDateInput />}
      className="inputStyle"
    />
    <div>
      {touched &&
        ((error && <span>{error}</span>) ||
          (warning && <span>{warning}</span>))}
    </div>
  </div>
);


// component for rendering a single select (multi choice options)
const renderSingleSelect = ({
  input,
  data,
  valueField,
  textField,
  type,
  meta: { touched, error, warning },
}) => (
  <div>
    <SelectList
      {...input}
      onBlur={() => input.onBlur()}
      data={data}
      type={type}
      valueField={valueField}
      textField={textField}
    />
    {touched &&
      ((error && <span>{error}</span>) || (warning && <span>{warning}</span>))}
  </div>
);

// component for rendering a multi select (dropdown, select many)
const renderManySelect = ({
  input,
  data,
  valueField,
  textField,
  type,
  meta: { touched, error, warning },
}) => (
  <div>
    <Multiselect
      {...input}
      busy
      busySpinner={<img src={caretDown} alt="SCH" />}
      onBlur={() => input.onBlur()}
      value={input.value || []} // requires value to be an array
      data={data}
      type={type}
      valueField={valueField}
      textField={textField}
    />
    <div>
      {touched &&
        ((error && <span>{error}</span>) ||
          (warning && <span>{warning}</span>))}
    </div>
  </div>
);

// component to make the group
const Group = ({ data }) => (
  <React.Fragment>
    <Row>
      <Col>
        {data.item.map(item => (
          <Item key={item.linkId} data={item} group={data.linkId} />
        ))}{' '}
      </Col>
    </Row>
  </React.Fragment>
);

// component to update the group section with the new questions and new url path
const GroupRoute = ({ data, ...rest }) => {
  const questionnaireName = getCurrentQuestionnaireName();
  return (
    <ListeningRoute
      path={`/questionnairePage/${questionnaireName}/group/${data.linkId}`}
      data={data}
      render={props => <Group {...props} data={data} {...rest} />}
    />
  );
};

// logic to check if the data being looked at is a group
// if yes, then group. if no, then single question
const Item = ({ data, group }) =>
  (data.type === 'group' ? (
    <FormGroup>
      <GroupRoute data={data} />
    </FormGroup>
  ) : (
    <EnableWhen
      enableWhenOptions={data.enableWhen}
      text={data.text}
      data={data}
    >
      <Question data={data} style={QuestionStyle} group={group} />
    </EnableWhen>
  ));

// validation for fields - all visible questions must be answered
const required = (value, allValues) => {
  
  // Validate against input which is simply just newlines
  if (value && typeof value === "string")
  {
    value = value.replace(/\n/g, "");
  }

  const isRequired = allValues !== undefined;
  const error = 'Please fill out this field';

  const output =
    isRequired && !value ? (
      <b style={{ color: 'rgb(255,0,0)' }}>{error}</b>
    ) : null;

  return output;
};

// validation for number only answers
const integer = (value) => {
  const numbers = /^[0-9]+$/;
  const error = 'This field must be a number';
  const output =
    !numbers.test(value) && value ? (
      <b style={{ color: 'rgb(255,0,0)' }}>{error}</b>
    ) : null;

  return output;
};

// question logic based on their type
const Question = ({ data, group }) => {
  if (data.type === 'string') {
    return <StringQuestion data={data} group={group} />;
  } else if (data.type === 'text') {
    return <TextQuestion data={data} group={group} />;
  } else if (data.type === 'date') {
    return <DateQuestion data={data} group={group} />;
  } else if (data.type === 'choice') {
    return <ChoiceQuestion data={data} group={group} />;
  } else if (data.type === 'integer') {
    return <IntegerQuestion data={data} group={group} />;
  }

  return null;
};

// question logic to sort out a single or many choice select
const ChoiceQuestion = ({ data, group }) =>
  (data.repeats === true ? (
    <ManyChoice data={data} group={group} />
  ) : (
    <SingleChoice data={data} group={group} />
  ));

// rendering a question that is a string input
const StringQuestion = ({ data, group }) => (
  <Row style={QuestionStyle}>
    <Col xs="4">{data.text}</Col>
    <Col xs="5">
      <Input
        class="test"
        tag={Field}
        name={`${group}${data.linkId}`}
        id={data.linkId}
        component={renderInput}
        validate={required}
        autoComplete="new-password"
        type="text"
        style={{ width: '100%' }}
      />
    </Col>
  </Row>
);

// rendering a question that is a text comment input
const TextQuestion = ({ data, group }) => (
  <Row style={QuestionStyle}>
    <Col xs="4">{data.text}</Col>
    <Col xs="5">
      <Input
        class="test"
        tag={Field}
        autoComplete="new-password"
        name={`${group}${data.linkId}`}
        id={data.linkId}
        component={renderInput}
        validate={required}
        type="textarea"
        style={{ width: '100%' }}
      />
    </Col>
  </Row>
);

// rendering a question that is an integer input
const IntegerQuestion = ({ data, group }) => (
  <Row style={QuestionStyle}>
    <Col xs="4">{data.text}</Col>
    <Col xs="5">
      <Input
        tag={Field}
        name={`${group}${data.linkId}`}
        autoComplete="new-password"
        id={data.linkId}
        component={renderInput}
        validate={[required, integer]}
        type="text"
        style={{ width: '100%' }}
      />
    </Col>
  </Row>
);

// rendering a question that is a date input
const DateQuestion = ({ data, group }) => (
  <Row style={QuestionStyle}>
    <Col xs="4">{data.text}</Col>
    <Col xs="5">
      <Input
        tag={Field}
        name={`${group}${data.linkId}`}
        autoComplete="new-password"
        id={data.linkId}
        component={renderDateSelect}
        validate={required}
        type="date"
        style={{ width: '100%' }}
      />
    </Col>
  </Row>
);

// rendering a question that is a many select input (dropdown list)
const ManyChoice = ({ data, group }) => (
  <Row style={QuestionStyle}>
    <Col xs="4">{data.text}</Col>
    <Col xs="5">
      <Input
        tag={Field}
        name={`${group}${data.linkId}`}
        autoComplete="new-password"
        id={data.linkId}
        component={renderManySelect}
        validate={required}
        data={data.options}
        valueField="id"
        textField="display"
      />
    </Col>
  </Row>
);

// rendering a question that is a single select input (multi choice)
const SingleChoice = ({ data, group }) => (

  <Row style={QuestionStyle}>

    <Col xs="4">{data.text}</Col>
    <Col xs="5">
      <Input
        tag={Field}
        name={`${group}${data.linkId}`}
        autoComplete="new-password"
        id={data.linkId}
        component={renderSingleSelect}
        validate={required}
        data={data.options}
        valueField="id"
        textField="display"
      />
    </Col>
  </Row>


);

// rendering a form button for savinge
const FormButton = ({
  pristine, submitting, handleSave, valid,
}) => {

  const URL = window.location.href.split('/').pop();
  if (URL !== 'W') {
    return (
      <React.Fragment>
        <Button
          type="submit"
          className="form-submit"
          color="success"
          disabled={!valid}
          onClick={handleSave}
        >
          Save & Continue
        </Button>
      </React.Fragment>
    );
  }
  return null;
};

// back button functionality
const BackButton = ({ handleBack }) => {
  const URL = window.location.href.split('/').pop();
  if (URL !== 'A') {
    return (
      <Button color="danger" onClick={handleBack}>
        Back
      </Button>
    );
  }
  return null;
};

// next button functionality
// const NextButton = ({ handleNext }) => {
//   const URL = window.location.href.split('/').pop();
//   if (URL !== 'W') {
//     return (
//       <Button color="primary" onClick={handleNext}>
//         Next
//       </Button>
//     );
//   }
//   return null;
// };

// if data has an item (array) then map it to Item
const Questionnaire = (props) => {
  const {
    data = { item: [] },
    pristine,
    submitting,
    handleSave,
    handleBack,
    // handleNext,
    valid,
  } = props;

  return (
    <Container className="Qu" fluid>
      <form name="DTT" onSubmit={e => e.preventDefault()} autoComplete="off">
        {data.item
          ? data.item.map(item => <Item key={item.linkId} data={item} />)
          : null}
        <div className="form-buttons">
          <BackButton handleBack={handleBack} />
          <FormButton
            pristine={pristine}
            submitting={submitting}
            handleSave={handleSave}
            valid={valid}
          />
          {/* <NextButton handleNext={handleNext} /> */}
        </div>
      </form>
    </Container>
  );
};

// Proptypes for all components
ChoiceQuestion.defaultProps = {
  data: { repeats: true },
};

FormButton.propTypes = {
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
};
FormButton.defaultProps = {
  pristine: false,
  submitting: false,
};

renderInput.propTypes = InputProps;
renderDateSelect.propTypes = DateProps;
renderSingleSelect.propTypes = ChoiceProps;
renderManySelect.propTypes = ChoiceProps;
Group.propTypes = QuestionProps;
GroupRoute.propTypes = QuestionProps;
Item.propTypes = QuestionProps;
Question.propTypes = QuestionProps;
StringQuestion.propTypes = QuestionProps;
DateQuestion.propTypes = Question.Props;
IntegerQuestion.propTypes = QuestionProps;
TextQuestion.propTypes = QuestionProps;
ChoiceQuestion.propTypes = QuestionProps;
ManyChoice.propTypes = QuestionProps;
SingleChoice.propTypes = QuestionProps;
Questionnaire.propTypes = QuestionnaireProps;

export default reduxForm({
  form: 'DTT', // unique identifier for the form
  destroyOnUnmount: true,
  enableReinitialize: true,
  forceUnregisterOnUnmount: true,
  onSubmit: () => {},
  onSubmitSuccess: (result, props) => props.onSubmit(result),
})(Questionnaire);



// WEBPACK FOOTER //
// ./src/components/Questionnaire/Questionnaire.js